import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ArcMark } from '../ui/ArcMark';
import { ArrowLeft, CheckCircle } from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');

  const { forgotPassword, error: authError, successMessage, loading, clearError, clearSuccessMessage } = useAuth();
  const navigate = useNavigate();

  const handleNavigateToLogin = () => {
    clearError();
    clearSuccessMessage();
    setLocalError('');
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setLocalError('Please enter your email address.');
      clearSuccessMessage();
      return;
    }

    try {
      setLocalError('');
      await forgotPassword(email);
    } catch (err: any) {
      setLocalError('An error occurred. Please try again.');
    }
  };

  const displayError = localError || authError;

  return (
    <div className="pk-auth">
      <div className="pk-auth-pane">
        <div className="pk-auth-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ArcMark variant="current" size={24} style={{ color: 'var(--arc-accent)' }} />
            <span style={{ fontFamily: 'var(--arc-font-display)', fontWeight: 800, fontSize: 18, color: 'var(--arc-ink)' }}>ARC Portal</span>
          </div>

          <div>
            <h1 className="pk-h2">Forgot Password</h1>
            <p className="pk-sub" style={{ marginTop: 4 }}>Enter your email address and we'll send you a link to reset your password.</p>
          </div>

          {displayError && <div className="pk-note pk-note-danger">{displayError}</div>}

          {successMessage && (
            <div className="pk-note" style={{ background: 'var(--arc-success-soft)' }}>
              <CheckCircle size={16} style={{ color: 'var(--arc-success)' }} />
              <span className="pk-small">{successMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="pk-field">
              <label className="pk-field-label" htmlFor="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="pk-input"
              />
            </div>

            <button
              type="submit"
              disabled={!email || loading}
              className="pk-btn pk-btn-grad pk-btn-lg pk-btn-block"
            >
              {loading ? <span className="pk-spinner" style={{ borderTopColor: '#fff' }} /> : 'Send Reset Link'}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <button type="button" onClick={handleNavigateToLogin} className="pk-btn-text" style={{ margin: '0 auto' }}>
              <ArrowLeft size={14} /> Back to Login
            </button>
          </div>
        </div>
      </div>

      <div className="pk-auth-side">
        <span className="pk-label">ARC · Developer Portal</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h3 style={{ fontFamily: 'var(--arc-font-display)', fontWeight: 800, fontSize: 30, letterSpacing: '-.035em', lineHeight: 1.1, margin: 0, color: '#F7F5F3', maxWidth: '22ch' }}>
            One key, every model.
          </h3>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'rgba(247,245,243,.66)', maxWidth: '44ch' }}>
            Manage access, usage, and routing rules in one place — task determination and per-call accounting behind a single endpoint.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <span className="pk-mono" style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(247,245,243,.5)' }}>api.askarc.app</span>
          <span className="pk-mono" style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(247,245,243,.5)' }}>docs</span>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
